import { FC, MouseEvent, useState } from 'react';

import {
    Box,
    Icons,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Popover,
    Theme,
    Toolbar,
    Typography,
} from '@thirdspacelearning/library';

import { User } from '../../types';
import { PupilAvatarIcon } from '@assets/app';

const sx = {
    toolbar: {
        /**
         * zIndex of 1310 is necessary to put this component above the Dialog backdrop
         * to ensure logout is still possible. The Paper component in Dialog has zIndex 1300
         *
         * This means that other toolbars that want to take over this space must implement
         * the site background colour and set a zIndex of at least 1320. This component
         * implements a transparent background and no explicit width
         * to avoid interacting unfavourably with the SideNavigation component.
         */
        zIndex: 1310,
        position: 'fixed',
        top: 0,
        left: 'auto',
        right: 0,
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
    },
    avatar: {
        '&:hover, &:focus': {
            cursor: 'pointer',
        },
    },
    avatarIcon: {
        width: '35px',
        height: '35px',
        color: 'transparent',
    },
    paper: (theme: Theme) => ({
        overflowX: 'unset',
        overflowY: 'unset',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '50%',
            width: 10,
            height: 10,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[1],
            transform: 'translate(0, -50%) rotate(315deg)',
            clipPath:
                'polygon(-5px -5px, calc(100% + 5px) -5px, calc(100% + 5px) calc(100% + 5px))',
        },
    }),
    white: {
        color: 'white',
    },
};

interface Props {
    user: User | null;
    logout: () => void;
    dialogOpen: boolean;
}

const ActionsBar: FC<Props> = ({ user, logout, dialogOpen }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const onAvatarClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <Toolbar variant="dense" sx={sx.toolbar}>
            <Box
                sx={sx.avatar}
                display="flex"
                alignItems="center"
                onClick={onAvatarClick}
            >
                <Box
                    sx={[dialogOpen && sx.white]}
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-end"
                    mr={1}
                    data-testid="logoutContainer"
                >
                    <Typography variant="caption" noWrap>
                        Not
                        <Box fontWeight="fontWeightBold" component="span">
                            {' ' + (user && user.first_name)}
                        </Box>
                        ? Log out
                    </Typography>
                </Box>
                <PupilAvatarIcon sx={sx.avatarIcon} />
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                sx={sx.paper}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <List>
                    <ListItem key="sign-out" onClick={logout} button>
                        <ListItemIcon>
                            <Icons.MeetingRoom />
                        </ListItemIcon>
                        <ListItemText primary="Sign Out" />
                    </ListItem>
                </List>
            </Popover>
        </Toolbar>
    );
};

export default ActionsBar;
